import * as React from 'react';
import { userType, recipientType } from '../../types/Types';
import { useEffect } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import AppBar from '../organisms/AppBar';
import Drawer from '../organisms/Drawer';
import Copyright from '../molecules/Copyright';
import Recipients from '../organisms/Recipients';
import ResponseTextContent from '../organisms/ResponseText';
import {
  Button,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useState } from 'react';
import Log from '../../lib/log';
import { v2api } from '../../api/axios';
import { useSelector } from '../../store/store';
import { useDispatch } from 'react-redux';
import { setUser } from '../../store/loginUserSlice';
import { useNavigate } from 'react-router';
import CommonDialog from '../atoms/CommonDialog';

// ログの初期化
const log = new Log('IncomingSetting');

function IncomingSettingContent() {
  log.putLog('info', 'IncomingSetting started.');

  // ページ遷移
  const navigate = useNavigate();

  // ストアからユーザー情報を取得
  const user = useSelector((state) => state.loginUser.user);
  const dispatch = useDispatch();

  // 最新のユーザー情報を取得する
  const [currentUser, setCurrentUser] = useState<userType | undefined>();
  useEffect(() => {
    (async () => {
      try {
        // 最新のユーザー情報を取得する
        const postData = {
          userId: user.id,
          token: user.access_token,
        };
        const res = await v2api.post('/get-user', postData);
        if (res.data.statusCode === 200) {
          // 取得成功
          const body = JSON.parse(res.data.body);
          setCurrentUser(body);
        } else {
          throw new Error(`getUser error: ${res.data.statusCode}`);
        }
      } catch (err: any) {
        log.putLog('error', err.message ? err.message : err);
        // ストアを初期化
        dispatch(setUser(''));
      }
    })();
  }, [user, dispatch]);

  // 応答メッセージ
  const handleResponseTextChang = (responseText: string) => {
    const newUser = {
      ...currentUser,
      response_text: responseText,
    };
    setCurrentUser(newUser);
  };

  // 音声タイプ
  const handleVoiceTypeChange = (voiceType: number) => {
    const newUser = {
      ...currentUser,
      response_voice_type: voiceType,
    };
    setCurrentUser(newUser);
  };

  // 追加メッセージ
  const handleAddMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUser = {
      ...currentUser,
      add_message: e.target.checked ? 1 : 0,
    };
    setCurrentUser(newUser);
  };

  // 送信先
  const handleRecipientChange = (idx: number, recipient: recipientType) => {
    const newRecipients = currentUser?.recipients?.map((r, pos) => {
      if (pos === idx) {
        return recipient;
      } else {
        return r;
      }
    });
    setCurrentUser({
      ...currentUser,
      recipients: newRecipients,
    });
  };

  // 送信先が削除されたとき
  const handleRecipientDelete = (idx: number) => {
    const newRecipients = currentUser?.recipients?.filter(
      (recipient, pos) => idx !== pos,
    );
    // const newRecipients = currentUser.recipients.splice(idx, 1);
    setCurrentUser({
      ...currentUser,
      recipients: newRecipients,
    });
  };

  // ダイヤログ
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  // キャンセルボタンが押されたとき
  const handleCancelClick = (e: any) => {
    navigate('/');
  };

  // 保存ボタンが押されたとき
  const handleSaveClick = (e: any) => {
    // 応答メッセージエラーチェック
    if (currentUser?.response_text?.trim() === '') {
      alert('応答メッセージが入力されていません。');
      return -1;
    }
    // メール送信先エラーチェック
    const error = currentUser?.recipients?.filter(
      (recipient) =>
        recipient.address === '' ||
        !recipient.address?.match(
          /^[a-zA-Z0-9.!#$%&'*+\\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
        ),
    );
    if (error && error.length > 0) {
      alert('メールアドレスが不正です。');
      return -1;
    }
    // メールアドレスの重複チェック
    const addresses: string[] = [];
    currentUser?.recipients?.map((recipient) =>
      addresses.push(recipient.address || ''),
    );
    const newAddresses = new Set(addresses);
    if (newAddresses.size !== addresses.length) {
      alert('メールアドレスが重複しています。');
      return -1;
    }
    setSaveDialogOpen(true);
  };
  const handleConfirm = async (result: boolean) => {
    setSaveDialogOpen(false);
    if (result) {
      try {
        // ユーザー情報を更新する
        const postData = {
          userId: currentUser?.id,
          token: currentUser?.access_token,
          action: 'updateProfile',
          input: {
            add_message: currentUser?.add_message,
            recipients: currentUser?.recipients?.map((r) => ({
              address: r.address,
              attach_media: r.attach_media,
              in_message: r.in_message,
              incoming_notice: r.incoming_notice,
            })),
          },
        };
        const res = await v2api.post('/update-db', postData);
        if (res.data.statusCode === 200) {
          // 応答メッセージの更新が必要かを確認
          if (
            currentUser?.response_text !== user.response_text ||
            currentUser?.response_voice_type !== user.response_voice_type
          ) {
            // 応答メッセージの更新
            await updateResponse();
          }
          // 更新成功
          dispatch(setUser(currentUser));
          navigate('/');
        } else {
          throw new Error(`update-db error: ${res.data.statusCode}`);
        }
      } catch (err: any) {
        log.putLog('error', err.message ? err.message : err);
        // ストアを初期化
        dispatch(setUser(''));
      }
    }
  };

  // 応答メッセージの更新
  const updateResponse = async () => {
    const postData = {
      userId: currentUser?.id,
      token: currentUser?.access_token,
      text: currentUser?.response_text,
      voiceType: currentUser?.response_voice_type?.toString(),
    };
    const res = await v2api.post('/change-response', postData);
    if (res.data.statusCode === 200) {
      // 取得成功
      return true;
    } else {
      throw new Error(`change-response error: ${res.data.statusCode}`);
    }
  };

  // メール送信先の追加
  const handleAddressAddClick = (e: any) => {
    const newRecipients = currentUser?.recipients;
    const newRecipient: recipientType = {
      address: '',
      attach_media: 1,
      in_message: 1,
      incoming_notice: 1,
    };
    newRecipients?.push(newRecipient);
    const newUser = {
      ...currentUser,
      recipients: newRecipients,
    };
    setCurrentUser(newUser);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar title='着信設定' />
      <Drawer />
      <Box
        component='main'
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
          <CommonDialog
            openDialog={saveDialogOpen}
            title={'更新確認'}
            message={'このデータを保存しますか？'}
            handleConfirm={(result) => handleConfirm(result)}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={6}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 550,
                }}
              >
                <Typography
                  variant='h6'
                  gutterBottom
                  component='div'
                  color={'gray'}
                >
                  応答メッセージ
                </Typography>
                <ResponseTextContent
                  responseText={currentUser?.response_text}
                  responseVoiceType={currentUser?.response_voice_type || 0}
                  handleResponseTextChange={handleResponseTextChang}
                  handleVoiceTypeChange={handleVoiceTypeChange}
                />
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentUser?.add_message === 1}
                        onChange={handleAddMessageChange}
                      />
                    }
                    label={
                      <Typography fontSize={14}>
                        音声認識確認メッセージの追加
                      </Typography>
                    }
                  />
                </FormGroup>
                <Typography fontSize={14} color='GrayText'>
                  このスイッチをONにすると、応答メッセージのあとに音声認識がされる旨のガイダンスが流れます。
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 550,
                }}
              >
                <Typography
                  variant='h6'
                  gutterBottom
                  component='div'
                  color={'gray'}
                >
                  メール送信先
                  <IconButton onClick={handleAddressAddClick}>
                    <AddCircleOutlineIcon fontSize='small' color='error' />
                  </IconButton>
                </Typography>
                <Recipients
                  recipients={currentUser?.recipients}
                  handleRecipientChange={(idx, recipient) =>
                    handleRecipientChange(idx, recipient)
                  }
                  handleRecipientDelete={(idx) => handleRecipientDelete(idx)}
                />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Grid container justifyContent='center' spacing={2}>
                <Grid item>
                  <Button
                    variant='outlined'
                    color={'primary'}
                    onClick={handleSaveClick}
                  >
                    保存
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant='outlined'
                    color={'error'}
                    onClick={handleCancelClick}
                  >
                    キャンセル
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Copyright sx={{ pt: 4 }} />
        </Container>
      </Box>
    </Box>
  );
}

export default function IncomingSetting() {
  return <IncomingSettingContent />;
}
